import React, { useEffect, useState } from 'react';
import './App.css';
import { XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import './fonts.css';
import axios from 'axios';
const API_URL = process.env.REACT_APP_API_URL || '';

const PRODUCTS_GOAL = 36;
const CONTACT_URL = 'https://t.me/nwwssr';
const UTM_PARAMS = 'utm_source=personal_website&utm_medium=card&utm_campaign=portfolio';

interface GraphPoint {
  ym: string;
  t: number;
  month: string;
  label: string;
  users: number;
}

interface Metric {
  total: number;
  graphData: GraphPoint[];
  [extra: string]: any;
}

type Metrics = { [name: string]: Metric | null | undefined };

interface Product {
  image: string;
  title: string;
  description: string;
  url: string;
  buttonText: string;
  // имя метрики в /api/stats; без него карточка всегда с кнопкой
  stat?: string;
  headline?: (metric: Metric) => string;
  // подпись, которая не зависит от статистики
  staticHeadline?: string;
  unit?: string;
}

interface Section {
  title: string;
  products: Product[];
}

const fmt = (value: number) => value.toLocaleString('en-US');

const STATS_TIMEOUT_MS = 15000;

// Ответ API не под нашим контролем на 100%: метрика с нечисловым итогом или без
// ряда считается отсутствующей, чтобы одна кривая цифра не роняла всю страницу.
const usable = (metric: any): Metric | null => {
  if (!metric || !Number.isFinite(metric.total) || !Array.isArray(metric.graphData)) return null;
  const graphData = metric.graphData.filter((point: any) => point && Number.isFinite(point.t) && Number.isFinite(point.users));
  return { ...metric, graphData };
};

const compact = (value: number) => {
  const trim = (n: number) => n.toFixed(1).replace(/\.0$/, '');
  if (value >= 1000000) return `${trim(value / 1000000)}M`;
  if (value >= 1000) return `${trim(value / 1000)}K`;
  return `${value}`;
};

const SECTIONS: Section[] = [
  {
    title: 'Current Active Products',
    products: [
      {
        image: 'Designflex.png',
        title: 'Designflex',
        staticHeadline: '240+ AI projects delivered',
        description: 'AI integrator for business and government: audit, local AI systems, implementation and support',
        url: 'https://designflex.ru',
        buttonText: 'Create my AI bot',
      },
      {
        image: 'Neuromentor.png',
        title: 'Neuromentor',
        description: 'Personal AI mentor that can use any book, text or video to teach you',
        url: 'https://neuromentor.ai',
        buttonText: 'Start learning',
        stat: 'neuromentor',
        headline: (m) => `${fmt(m.total)} students`,
        unit: 'students',
      },
      {
        image: 'NeuroAesthetics.png',
        title: 'NeuroAesthetics',
        description: 'Honest look at your aesthetics by a computer vision AI',
        url: 'https://t.me/neuroaesthetic_bot',
        buttonText: 'Open bot',
        stat: 'neuroaesthetics',
        headline: (m) => `${fmt(m.total)} users checked their aesthetics`,
        unit: 'users',
      },
      {
        image: 'GrabTrackBot.png',
        title: 'GrabTrackBot',
        description: 'Sends music track from player screenshot, Spotify link, text or voice',
        url: 'https://t.me/grabtrackbot',
        buttonText: 'Open bot',
        stat: 'grabtrackbot',
        headline: (m) => `${fmt(m.total)} tracks delivered${m.users ? ` to ${fmt(m.users)} users` : ''}`,
        unit: 'tracks',
      },
      {
        image: 'AdminTools.jpg',
        title: 'AdminTools',
        description: 'AI toolkit for Telegram channel admins: audience analysis, comment TL;DR, anti-spam',
        url: 'https://t.me/ChanToolsBot',
        buttonText: 'Open bot',
        stat: 'admintools',
        headline: (m) => `${fmt(m.total)} channels analyzed`,
        unit: 'channels',
      },
      {
        image: 'NeuroDating.png',
        title: 'NeuroDating',
        description: 'First AI dating',
        url: 'https://t.me/DatingNeuroBot',
        buttonText: 'Open bot',
        stat: 'neurodating',
        headline: (m) => `${fmt(m.total)} profiles${m.matches ? ` · ${fmt(m.matches)} matches` : ''}`,
        unit: 'profiles',
      },
      {
        image: 'MyTulpa.jpg',
        title: 'MyTulpa',
        description: 'AI companion with her own character, memory and a diary about you',
        url: 'https://t.me/mytulpawaifubot',
        buttonText: 'Open bot',
        stat: 'mytulpa',
        headline: (m) => `${fmt(m.total)} users`,
        unit: 'users',
      },
      {
        image: 'Neurohistoria.png',
        title: 'Neurohistoria',
        description: 'Alternate history simulator: rule a country in a world run by an LLM',
        url: 'https://neurohistoria.ru',
        buttonText: 'Play',
        stat: 'neurohistoria',
        headline: (m) => `${fmt(m.total)} worlds played`,
        unit: 'worlds',
      },
      {
        image: 'WhatTheNoise.jpg',
        title: 'WHAT THE NOISE?',
        description: 'Guess-the-genre music quiz on Every Noise data in a Winamp skin',
        url: 'https://whatthenoise.nwsr.in',
        buttonText: 'Play',
        stat: 'whatthenoise',
        headline: (m) => `${fmt(m.total)} players`,
        unit: 'players',
      },
      {
        image: 'NeuroSummary.png',
        title: 'NeuroSummary',
        description: 'Transcribes audio in telegram chats and gives summary for last messages',
        url: 'https://t.me/NeuroSummaryBot',
        buttonText: 'Open bot',
        stat: 'neurosummary',
        headline: (m) => (m.active && m.active !== m.total
          ? `${fmt(m.total)} chats · ${fmt(m.active)} active now`
          : `${fmt(m.total)} active chats`),
        unit: 'chats',
      },
      {
        image: 'KanjiPocket.png',
        title: 'Kanji Pocket',
        description: 'Draw a kanji with a finger or stylus: reading, meaning, JLPT level and words with furigana, offline',
        url: 'https://kanji.nwsr.in',
        buttonText: 'Open app',
      },
      {
        image: 'GrabStreamBot.png',
        title: 'GrabStreamBot',
        description: 'Downloads from YouTube, TikTok, Reels and 100+ sources in Telegram',
        url: 'https://t.me/GrabStreamBot',
        buttonText: 'Open bot',
      },
      {
        image: 'Neurotaro.jpg',
        title: 'Neurotaro',
        description: 'Tarot readings by an AI that explains every card in your spread',
        url: 'https://t.me/TaroNeuroBot',
        buttonText: 'Open bot',
      },
      {
        image: 'Radar.png',
        title: 'Radar',
        description: 'Marketing engine that links demand, ads and sales into one workflow',
        url: 'https://radar.designflex.ru',
        buttonText: 'Visit site',
      },
      {
        image: 'Balance.png',
        title: 'Balance',
        description: 'AI food diary: calories from a photo, weight and body measurements',
        url: 'https://aidiet.designflex.ru',
        buttonText: 'Open app',
      },
      {
        image: 'SubGenerator.png',
        title: 'SubGenerator',
        description: 'Transcribes video and generates YouTube subtitles in 10+ languages',
        url: 'https://boosty.to/nwsr',
        buttonText: 'Get on Boosty',
      },
      {
        image: 'YTDownloader.png',
        title: 'YTDownloader',
        description: 'Downloads YouTube videos via desktop app with beautiful UI',
        url: 'https://boosty.to/nwsr/posts/21b691fa-ff8b-490b-b6fe-128c3edbec30',
        buttonText: 'Get on Boosty',
      },
    ],
  },
  {
    title: 'In Development',
    products: [
      {
        image: 'Potok.png',
        title: 'Potok',
        description: 'One inbox for Telegram, MAX and Avito that turns chats into tasks',
        url: CONTACT_URL,
        buttonText: 'Notify me',
      },
      {
        image: 'Sovetnik.png',
        title: 'Sovetnik',
        description: 'macOS copilot for calls: live transcript, hints and checks against your documents',
        url: CONTACT_URL,
        buttonText: 'Notify me',
      },
      {
        image: 'NeuroQuests.png',
        title: 'NeuroQuests',
        description: 'Bot that turns your chores into a fun experience',
        url: CONTACT_URL,
        buttonText: 'Notify me',
      },
      {
        image: 'Neuroheadhunter.png',
        title: 'Neuroheadhunter',
        description: 'The only AI job parser that truly understands your career goals and finds your best fit',
        url: CONTACT_URL,
        buttonText: 'Notify me',
      },
    ],
  },
  {
    title: 'Media',
    products: [
      {
        image: 'NWSR_Cyberculture.png',
        title: 'NWSR / Cyberculture',
        description: 'Telegram blog about AI, tech and design',
        url: 'https://t.me/nwsrverse',
        buttonText: 'Read',
        stat: 'nwsr_blog',
        headline: (m) => `${fmt(m.total)} subscribers`,
        unit: 'subscribers',
      },
      {
        image: 'YouTube.png',
        title: 'YouTube',
        description: 'YouTube channel about technology and videogames',
        url: 'https://www.youtube.com/@nwwssr/videos',
        buttonText: 'Watch',
        stat: 'youtube',
        headline: (m) => `${fmt(m.total)} views`,
        unit: 'views',
      },
      {
        image: 'TikTok.png',
        title: 'TikTok',
        description: 'TikTok channel about AI, tech and design',
        url: 'https://tiktok.com/@nwwssr',
        buttonText: 'Watch',
        stat: 'tiktok',
        headline: (m) => `${fmt(m.total)} likes`,
        unit: 'likes',
      },
    ],
  },
];

const PRODUCTS_COUNT = SECTIONS.reduce((sum, section) => sum + section.products.length, 0);

const EXPERIENCE: { role: string; since?: number; years?: number }[] = [
  { role: 'Entrepreneur', since: 2018 },
  { role: 'Ex Head of Design', years: 3 },
  { role: 'Product Designer', since: 2009 },
  { role: 'AI Developer', since: 2022 },
];

const getProductUrl = (product: Product) =>
  `${product.url}${product.url.includes('?') ? '&' : '?'}${UTM_PARAMS}`;

const handleCardClick = (product: Product) => {
  window.open(getProductUrl(product), '_blank', 'noopener');
};

const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
  const card = e.currentTarget;
  const rect = card.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;

  const centerX = rect.width / 2;
  const centerY = rect.height / 2;

  const rotateX = (y - centerY) / 20;
  const rotateY = -(x - centerX) / 20;

  card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(10px)`;
};

const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
  e.currentTarget.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) translateZ(0)';
};

const AXIS_TICK = {
  fill: 'rgba(21, 21, 21, 0.40)',
  fontSize: 10,
  fontFamily: 'SF Pro',
  fontWeight: 590,
  letterSpacing: '-0.24px',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ProductGraph: React.FC<{ id: string; data: GraphPoint[]; unit: string }> = ({ id, data, unit }) => {
  const byTime = new Map(data.map((point) => [point.t, point]));

  // t — номер месяца (год * 12 + месяц), поэтому подпись считается для любой отметки оси.
  // Год показываем на краях оси и у января, остальным хватает месяца.
  const formatTick = (t: number) => {
    const month = MONTHS[t % 12];
    const withYear = t === data[0].t || t === data[data.length - 1].t || t % 12 === 0;
    return withYear ? `${month} '${String(Math.floor(t / 12)).slice(2)}` : month;
  };

  return (
    <div className="product-graph">
      <ResponsiveContainer width="100%" height={100}>
        <AreaChart data={data} margin={{ top: 10, right: 22, left: -20, bottom: -10 }}>
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="3.74%" stopColor="rgba(120, 96, 255, 0.40)" />
              <stop offset="99.23%" stopColor="rgba(255, 255, 255, 0.40)" />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="t"
            type="number"
            domain={['dataMin', 'dataMax']}
            tickCount={4}
            allowDecimals={false}
            tickFormatter={formatTick}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            domain={[0, 'auto']}
            tickCount={3}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            tickFormatter={compact}
          />
          <Tooltip
            separator=""
            labelFormatter={(t) => byTime.get(Number(t))?.label || ''}
            formatter={(value) => [`${fmt(Number(value))} ${unit}`, '']}
            contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 20px rgba(21, 21, 21, 0.14)', padding: '6px 10px' }}
            labelStyle={{ color: 'rgba(21, 21, 21, 0.40)', fontSize: 11, fontWeight: 590 }}
            itemStyle={{ color: '#7860ff', fontSize: 12, fontWeight: 590, padding: 0 }}
          />
          <Area type="monotone" dataKey="users" stroke="#7860ff" fill={`url(#${id})`} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const App: React.FC = () => {
  // null — статистика ещё не пришла; карточки рисуем после ответа, чтобы они не прыгали по высоте
  const [metrics, setMetrics] = useState<Metrics | null>(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/stats`, { timeout: STATS_TIMEOUT_MS })
      .then((response) => setMetrics(response.data?.metrics || {}))
      .catch((error) => {
        // без статистики карточки остаются с кнопками: выдуманных цифр не показываем
        console.error('Error fetching stats:', error);
        setMetrics({});
      });
  }, []);

  const progress = Math.min(100, (PRODUCTS_COUNT / PRODUCTS_GOAL) * 100);
  const currentYear = new Date().getFullYear();

  return (
    <div className="container">
      <div className="sidebar">
        <img src={require('./assets/avatar.png')} alt="Ruslan Gibadullin" className="avatar" />
        <h1 className="title">Ruslan Gibadullin</h1>
        <p className="subtitle">Building {PRODUCTS_GOAL} products</p>
        <div className="progress-wrapper">
            <div className="progress-counter" style={{ left: `${progress}%` }}>
                <span className="current">{PRODUCTS_COUNT}</span>
                <span className="total">/{PRODUCTS_GOAL}</span>
            </div>
            <div className="progress-bar-container">
                <div className="progress-bar" style={{ width: `${progress}%` }}></div>
            </div>
        </div>
        <div className="experience">
            <h3>Experience</h3>
            <ul>
                {EXPERIENCE.map((item) => (
                  <li key={item.role}>{item.role} <span>{item.years ?? currentYear - (item.since as number)} yrs</span></li>
                ))}
            </ul>
        </div>
      </div>
      <div className="main-content">
        {metrics && SECTIONS.map((section) => (
          <React.Fragment key={section.title}>
            <h2>{section.title}</h2>
            <div className="products">
              {section.products.map((product, index) => {
                const metric = product.stat ? usable(metrics[product.stat]) : null;
                const hasGraph = !!metric && metric.graphData.length > 1;
                return (
                  <div
                    className="product-card"
                    onClick={() => handleCardClick(product)}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                    style={{"--index": index} as React.CSSProperties}
                    key={product.title}
                  >
                    <div className="card-content">
                      <div className="card-header">
                        <img src={require(`./assets/${product.image}`)} alt={product.title} className="product-image" />
                        <div className="title-descriptor">
                          <h3 className="product-title">{product.title}</h3>
                          {metric && product.headline ? (
                            <p className="product-users-count">{product.headline(metric)}</p>
                          ) : product.staticHeadline && (
                            <p className="product-users-count">{product.staticHeadline}</p>
                          )}
                          <p className="product-description">{product.description}</p>
                        </div>
                      </div>
                      {hasGraph && metric ? (
                        <ProductGraph id={`graph-${product.stat}`} data={metric.graphData} unit={product.unit || ''} />
                      ) : (
                        <button className="action-button">{product.buttonText}</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default App;
